import type { Slot } from './types'
import { nowMs } from './clock'

/**
 * Slot timing.
 *
 * ## What the API actually sends
 *
 * A real `/getSlot` response contains, for one and the same row:
 *
 *     slot_begin: "2025-09-10T20:30:00.000Z"
 *     slot_time:  "8:30PM - 10:30PM"
 *
 * `slot_time` is produced by MSSQL itself (`CAST(slot.slot_begin AS TIME)` inside the
 * select), so the column really does hold 20:30 and that 20:30 means half past eight
 * in the evening, Malaysian time. The driver then serialises it with a `Z` suffix it
 * has not earned: the digits are local wall-clock, the label claims UTC.
 *
 * ## Why the server's own window check never fires
 *
 * `server.js` does `new Date(`${slot.slot_begin}+08:00`)`, which on this data yields
 * the string "2025-09-10T20:30:00.000Z+08:00" — a value carrying both `Z` and an
 * offset, which is not valid ISO 8601. It parses to Invalid Date, so `getTime()` is
 * NaN and both halves of the range comparison are false. The consequence is that
 * `onGoing` comes back empty and `show_btn` comes back false for every slot, always,
 * for every event. The sample response confirms it: `"onGoing": []` with all seven
 * slots `show_btn: false`.
 *
 * So this module cannot lean on the server for timing. It reads the raw strings, and
 * `canCheckIn()` below computes the unlock window itself.
 */

const MYT_OFFSET_MS = 8 * 60 * 60 * 1000

const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i
const DATETIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
/** Leading clock reading of `slot_time`, e.g. "8:30PM - 10:30PM" -> 8, 30, PM. */
const CLOCK = /^\s*(\d{1,2}):(\d{2})\s*([AP]M)/i

/** Minutes past midnight described by the wall-clock digits of an ISO-ish string. */
function wallClockMinutes(value: string): number | null {
  const m = value.match(DATETIME)
  return m ? +m[4] * 60 + +m[5] : null
}

/** Minutes past midnight described by the leading time in `slot_time`. */
function labelMinutes(slotTime: string | null | undefined): number | null {
  const m = slotTime?.match(CLOCK)
  if (!m) return null
  const hour = +m[1] % 12
  const pm = m[3].toUpperCase() === 'PM'
  return (pm ? hour + 12 : hour) * 60 + +m[2]
}

/**
 * Parse a slot datetime to epoch ms.
 *
 * `label` is the matching `slot_time` string when one is available. It is used as a
 * witness: MSSQL formatted it from the same column, so when its clock reading agrees
 * with the digits in `value`, those digits are confirmed to be Malaysian wall-clock
 * and the trailing `Z` is a lie we correct for. When they disagree, the timestamp is
 * a genuine instant and is taken at face value — which is what would happen if the
 * backend's serialisation were ever fixed, so this keeps working either way.
 */
export function parseSlotTime(
  value: string | null | undefined,
  label?: string | null,
): number {
  if (!value) return Number.NaN
  const raw = String(value).trim()

  if (!DATETIME.test(raw)) return new Date(raw).getTime()

  const m = raw.match(DATETIME)!
  const [, y, mo, d, h, mi, s] = m
  const asWallClock = Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0) - MYT_OFFSET_MS

  // No zone marker at all: nothing to reconcile, the digits are wall-clock.
  if (!ZONED.test(raw)) return asWallClock

  const wall = wallClockMinutes(raw)
  const stated = labelMinutes(label)

  // No witness available — trust the observed backend behaviour, which is that the
  // digits are wall-clock MYT regardless of the suffix.
  if (stated === null || wall === null) return asWallClock

  return wall === stated ? asWallClock : new Date(raw).getTime()
}

/**
 * Render an instant as Malaysian wall-clock time, 24-hour.
 *
 * 24-hour throughout: it is what clinical timetables use, it removes the AM/PM
 * ambiguity around a 12:00 session, and four fixed digits align down the time rail
 * in a way "8:30PM" never will. Formatting is pinned to Asia/Kuala_Lumpur rather
 * than the device zone so a delegate whose phone is still on another country's time
 * sees the programme as printed.
 */
const mytClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const mytDay = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  weekday: 'long',
  day: '2-digit',
  month: 'long',
})

/*
 * h23 rather than the locale default, so midnight is hour 0 and not 24 - the
 * difference decides whether a 00:30 session is filed under Morning or nowhere.
 */
const mytHourOnly = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: '2-digit',
  hourCycle: 'h23',
})

/** Hour of the day in Malaysia, 0-23, or NaN if the slot's time would not parse. */
export function mytHour(epoch: number): number {
  return Number.isFinite(epoch) ? Number(mytHourOnly.format(epoch)) : NaN
}

export function formatClock(epoch: number): string {
  return Number.isFinite(epoch) ? mytClock.format(epoch) : '--:--'
}

export function formatDay(epoch: number): string {
  return Number.isFinite(epoch) ? mytDay.format(epoch) : ''
}

export interface SlotWindow {
  start: number
  /** Includes `extended_time`, which stretches the end of the window. */
  end: number
  /** The moment the check-in button unlocks. */
  unlocksAt: number
}

export function slotWindow(slot: Slot): SlotWindow {
  // slot_time reads "8:30PM - 10:30PM"; the half after the dash witnesses slot_end.
  const [beginLabel, endLabel] = String(slot.slot_time ?? '').split('-')

  const start = parseSlotTime(slot.slot_begin, beginLabel)
  const end =
    parseSlotTime(slot.slot_end, endLabel) +
    (Number(slot.extended_time) || 0) * 60_000

  // `initial_register` opens check-in for the whole window; otherwise it opens
  // `offset_checkin_minute` before the end, so nobody scans in and walks straight out.
  const unlocksAt = slot.initial_register
    ? start
    : end - (Number(slot.offset_checkin_minute) || 0) * 60_000

  return { start, end, unlocksAt }
}

/**
 * How long after a session's scheduled end check-in stays open.
 *
 * Sessions run over. A 09:00-11:00 talk that finishes at 11:15 used to lock everyone
 * who had not yet scanned out of the app at 11:01 exactly - not gradually, and with no
 * way back in, because the button is the only route to the scanner.
 *
 * This grace is deliberately client-side only, and it grants nothing the system did not
 * already allow: `/checkIN` performs no time check whatsoever - it matches `id_event` +
 * `qr_uuid` + `slot_id_toscan` - so the real gate has always been the QR on the wall,
 * which comes down when the room clears. All this does is stop the app from being
 * stricter than the thing it is a front end for.
 *
 * Twenty minutes covers the ordinary overrun plus the walk out of the room, and is
 * short enough that the wall code is usually still up. A session that runs an hour long
 * is a different problem and wants `slot.extended_time`, which both this module and the
 * server already read - but nothing in the API writes it today.
 */
export const CHECKIN_GRACE_MS = 20 * 60_000

export type SlotState = 'past' | 'live' | 'upcoming'

export function slotState(slot: Slot, now: number = nowMs()): SlotState {
  const { start, end } = slotWindow(slot)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 'upcoming'
  if (now > end) return 'past'
  if (now >= start) return 'live'
  return 'upcoming'
}

/**
 * Whether to offer the check-in button.
 *
 * Deliberately does not require the server's `show_btn`, which is unconditionally
 * false (see the note at the top of this file). `show_btn` is still honoured when it
 * is true, so this starts deferring to the server the moment the backend is fixed.
 *
 * Allowing the attempt is safe: `/checkIN` performs no time check of its own — it
 * matches `qr_uuid` + `slot_id_toscan` + `id_event` — so the QR code on the wall is
 * what actually gates a check-in, and that code only exists while the session runs.
 */
export function canCheckIn(slot: Slot, now: number = nowMs()): boolean {
  if (!slot.qr_required) return false
  if (slot.checkin_status) return false
  if (slot.show_btn) return true

  const { end, unlocksAt } = slotWindow(slot)
  if (!Number.isFinite(end) || !Number.isFinite(unlocksAt)) return false
  // `end + grace`, not `end`: see CHECKIN_GRACE_MS. `slotState` is deliberately not
  // widened - a session that has finished is past on the programme even while its
  // check-in is still open, and saying "live now" about a room people have left would
  // be a worse lie than the closed button was.
  return now >= unlocksAt && now <= end + CHECKIN_GRACE_MS
}

/** Why the button is not there yet — shown instead of leaving a dead space. */
export function checkInHint(slot: Slot, now: number = nowMs()): string | null {
  if (!slot.qr_required) return null
  if (slot.checkin_status) return null
  if (canCheckIn(slot, now)) return null

  const { end, unlocksAt } = slotWindow(slot)
  if (!Number.isFinite(unlocksAt)) return null
  // Only once the grace has run out too, otherwise this would contradict the button
  // canCheckIn is still showing. Inside the grace canCheckIn is true, so this function
  // has already returned null above.
  if (now > end + CHECKIN_GRACE_MS) return 'Check-in closed'

  const mins = Math.round((unlocksAt - now) / 60_000)
  if (mins <= 0) return 'Opening now'
  if (mins < 60) return `Opens in ${mins} min`
  const h = Math.floor(mins / 60)
  const rem = mins % 60
  return rem ? `Opens in ${h}h ${rem}m` : `Opens in ${h}h`
}

/** "in 25 min", "12 min left", "ended" — compact enough for a dense list row. */
export function relativeLabel(slot: Slot, now: number = nowMs()): string {
  const { start, end } = slotWindow(slot)
  if (!Number.isFinite(start)) return ''

  if (now < start) {
    const mins = Math.round((start - now) / 60_000)
    if (mins < 1) return 'starting now'
    if (mins < 60) return `in ${mins} min`
    const h = Math.floor(mins / 60)
    const rem = mins % 60
    if (h < 24) return rem ? `in ${h}h ${rem}m` : `in ${h}h`
    return `in ${Math.round(h / 24)}d`
  }

  if (now <= end) {
    const mins = Math.round((end - now) / 60_000)
    return mins < 1 ? 'ending' : `${mins} min left`
  }

  return 'ended'
}

/**
 * Session titles arrive with embedded CRLF from the CMS
 * ("Session 2:\r\n Revolutionising Healthcare:\r\n Malaysia's...").
 * Collapse it so headings wrap on their own terms.
 */
export function cleanTitle(name: string | null | undefined): string {
  return String(name ?? '').replace(/\s*[\r\n]+\s*/g, ' ').trim()
}

/**
 * Splits "Session 3: Digital Tools for Disease Prevention" into its label and title
 * so the two can be set differently. Returns a null label when there is no prefix.
 */
export function splitTitle(name: string | null | undefined): {
  label: string | null
  title: string
} {
  const clean = cleanTitle(name)
  const m = clean.match(/^(Session\s+\d+|Day\s+\d+|Workshop\s+\d+)\s*[:.\-–]\s*(.+)$/i)
  return m ? { label: m[1], title: m[2] } : { label: null, title: clean }
}

/** Groups a flat slot list into programme days, preserving chronological order. */
export function groupByDay(
  slots: Slot[],
): Array<{ key: string; label: string; slots: Slot[] }> {
  const byKey = new Map<string, { key: string; label: string; slots: Slot[]; at: number }>()

  for (const slot of slots) {
    const key = String(slot.slot_begin).slice(0, 10)
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: slot.slot_date_full || slot.slot_date_short || key,
        slots: [],
        at: slotWindow(slot).start,
      })
    }
    byKey.get(key)!.slots.push(slot)
  }

  const groups = [...byKey.values()]
  for (const group of groups) {
    group.slots.sort((a, b) => slotWindow(a).start - slotWindow(b).start)
  }
  groups.sort((a, b) => a.at - b.at)

  return groups.map(({ key, label, slots: s }) => ({ key, label, slots: s }))
}

/*
 * "Dr Suriani Sulaiman, FMS KK Presint 9" -> name and position, split at the first
 * comma. The data holds the two in one field, and one line of both wrapped badly on
 * a phone. No comma means it is all name.
 */
export function splitSpeaker(speaker: string | null | undefined): { name: string; position: string | null } {
  const text = (speaker ?? '').trim()
  const i = text.indexOf(',')
  if (i < 0) return { name: text, position: null }
  return { name: text.slice(0, i).trim(), position: text.slice(i + 1).trim() || null }
}
